import React, { useCallback, useEffect, useState } from "react";
import {
    Badge,
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Input,
    Row,
    Spinner,
    Table
} from "reactstrap";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import Api from "../../components/helpers/Api";
import SeoNoIndex from "../../components/helpers/SeoNoIndex";
import { sessionEnded } from "../user/SessionEnded";

function statusColor(status) {
    const colors = {
        ACTIVE: "success",
        RESTORED: "success",
        PAST_DUE: "warning",
        RESTRICTED: "warning",
        SUSPENDED: "danger",
        OPEN: "warning",
        CLOSED: "info",
        INVOICED: "success",
        PAID: "success",
        VOID: "secondary",
        UNCOLLECTIBLE: "danger",
        active: "success",
        trialing: "info",
        past_due: "warning",
        canceled: "danger"
    };
    return colors[status] || "secondary";
}

function formatDate(value) {
    if (!value) return "—";
    return new Date(value).toLocaleString("en-GB", {
        day: "numeric", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit"
    });
}

function short(value) {
    if (!value) return "—";
    return value.length > 26 ? `${value.slice(0, 14)}…${value.slice(-8)}` : value;
}

function Panel({ title, children }) {
    return (
        <Card className="shadow border-0 mb-4">
            <CardBody className="py-4">
                <h5 className="mb-3 text-uppercase" style={{ fontSize: "0.85rem", letterSpacing: "0.05em", color: "#525f7f" }}>{title}</h5>
                {children}
            </CardBody>
        </Card>
    );
}

function EmptyRow({ colSpan }) {
    return (
        <tr>
            <td colSpan={colSpan} className="text-muted text-center py-4">No records</td>
        </tr>
    );
}

export default function AdminAccountDetail() {
    const { accountId } = useParams();
    const navigate = useNavigate();
    const [unauthorized, setUnauthorized] = useState(false);
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [reason, setReason] = useState("");
    const [targetState, setTargetState] = useState("RESTRICTED");
    const [emailType, setEmailType] = useState("PAYMENT_FAILED");
    const [confirmName, setConfirmName] = useState("");

    useEffect(() => {
        try {
            const customer = sessionStorage.getItem("customer");
            const parsed = customer ? JSON.parse(customer) : null;
            if (!parsed?.user?.isAdmin) setUnauthorized(true);
        } catch {
            setUnauthorized(true);
        }
    }, []);

    const load = useCallback(() => {
        setLoading(true);
        setError("");
        Api.get(`/api/admin/operations/accounts/${accountId}`)
            .then(({ data }) => setDetail(data))
            .catch(() => setError("Unable to load account detail."))
            .finally(() => setLoading(false));
    }, [accountId]);

    useEffect(() => {
        if (!unauthorized) load();
    }, [load, unauthorized]);

    if (!sessionStorage.getItem("customer")) return sessionEnded();
    if (unauthorized) return <Navigate to="/" />;

    const action = (request) => {
        setBusy(true);
        setError("");
        request()
            .then(() => load())
            .catch(e => setError(e.response?.data?.message || "Action failed."))
            .finally(() => setBusy(false));
    };

    const restore       = () => action(() => Api.post(`/api/admin/operations/accounts/${accountId}/lifecycle/restore`, { reason }));
    const escalate      = () => action(() => Api.post(`/api/admin/operations/accounts/${accountId}/lifecycle/escalate`, { targetState, reason }));
    const closePeriod   = (periodId) => action(() => Api.post(`/api/admin/operations/accounts/${accountId}/billing-periods/${periodId}/close`, { reason }));
    const resyncStripe  = () => action(() => Api.post(`/api/admin/operations/accounts/${accountId}/stripe/resync`));
    const resyncInvoices = () => action(() => Api.post(`/api/admin/operations/accounts/${accountId}/invoices/resync`));
    const resendEmail   = () => action(() => Api.post(`/api/admin/operations/accounts/${accountId}/email/resend`, { emailType }));
    const revokeInvitation = (invitationId) => action(() => Api.post(`/api/admin/operations/accounts/${accountId}/invitations/${invitationId}/revoke`));

    const restoreAvailable   = ["PAST_DUE", "RESTRICTED", "SUSPENDED"].includes(detail?.enterpriseState);
    const escalationAvailable = ["PAST_DUE", "RESTRICTED"].includes(detail?.enterpriseState);

    return (
        <>
            <SeoNoIndex noindex={true} />

            <div className="position-relative">
                <section className="section section-lg section-shaped pb-250">
                    <div className="shape shape-style-1 shape-default">
                        <span /><span /><span /><span /><span /><span /><span /><span /><span />
                    </div>
                    <Container className="py-lg-md d-flex">
                        <div className="col px-0">
                            <Row>
                                <Col lg="8">
                                    <h1 className="display-3 text-white">
                                        Building your song-flows<br /><span>account detail</span>
                                    </h1>
                                </Col>
                            </Row>
                        </div>
                    </Container>
                    <div className="separator separator-bottom separator-skew">
                        <svg xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" viewBox="0 0 2560 100">
                            <polygon className="fill-white" points="2560 0 2560 100 0 100" />
                        </svg>
                    </div>
                </section>

                <section className="section section-lg pt-lg-0 mt--200">
                    <Container>
                        <Row className="justify-content-center">
                            <Col lg="12">
                                <Row className="row-grid">
                                    <Col lg="12">
                                        <Card className="shadow border-0">
                                            <CardBody className="py-5">

                        <Button color="link" onClick={() => navigate("/admin/operations")} className="mb-4 p-0">
                            &larr; Back to operations
                        </Button>

                        {error && <div className="alert alert-danger mb-4">{error}</div>}

                        {loading ? (
                            <div className="text-center py-5"><Spinner color="primary" /></div>
                        ) : detail && (
                            <>
                                {/* Account header */}
                                <div className="d-flex justify-content-between align-items-start mb-4">
                                    <div>
                                        <h2 className="mb-1">{detail.name}</h2>
                                        <p className="text-muted mb-0">
                                            {detail.accountType} account &middot; created {formatDate(detail.createdAt)}
                                        </p>
                                    </div>
                                    {detail.enterpriseState && (
                                        <Badge color={statusColor(detail.enterpriseState)} className="p-2" style={{ fontSize: "0.9rem" }}>
                                            {detail.enterpriseState}
                                        </Badge>
                                    )}
                                </div>

                                {/* Account overview */}
                                <Panel title="Account">
                                    <Row className="mb-3">
                                        <Col md="4">
                                            <span className="text-muted small text-uppercase">Subscription</span><br />
                                            {detail.subscriptionStatus
                                                ? <Badge color={statusColor(detail.subscriptionStatus)}>{detail.subscriptionStatus}</Badge>
                                                : <span className="text-muted">—</span>}
                                        </Col>
                                        <Col md="4">
                                            <span className="text-muted small text-uppercase">Active seats</span><br />
                                            <strong>{detail.activeSeats}{detail.maxSeats ? ` / ${detail.maxSeats}` : ""}</strong>
                                        </Col>
                                        <Col md="4">
                                            <span className="text-muted small text-uppercase">Members</span><br />
                                            <strong>{detail.memberCount}</strong>
                                        </Col>
                                    </Row>
                                    <Row className="mb-3">
                                        <Col md="4">
                                            <span className="text-muted small text-uppercase">Stripe customer</span><br />
                                            <code className="small">{short(detail.stripeCustomerId)}</code>
                                        </Col>
                                        <Col md="4">
                                            <span className="text-muted small text-uppercase">Group subscription</span><br />
                                            <code className="small">{short(detail.stripeSubscriptionId)}</code>
                                        </Col>
                                        <Col md="4">
                                            <span className="text-muted small text-uppercase">Admin subscription</span><br />
                                            <code className="small">{short(detail.stripeAdminSubscriptionId)}</code>
                                        </Col>
                                    </Row>
                                    <div>
                                        <Button size="sm" color="secondary" outline disabled={busy} onClick={resyncStripe} className="mr-2">
                                            Resync Stripe subscription
                                        </Button>
                                        <Button size="sm" color="secondary" outline disabled={busy} onClick={resyncInvoices}>
                                            Resync open invoices
                                        </Button>
                                    </div>
                                </Panel>

                                {/* Lifecycle actions */}
                                <Panel title="Lifecycle Actions">
                                    <Row>
                                        <Col md="5" className="mb-3">
                                            <Input
                                                type="textarea"
                                                rows="3"
                                                value={reason}
                                                onChange={e => setReason(e.target.value)}
                                                placeholder="Reason note — required for all state-changing actions"
                                            />
                                        </Col>
                                        <Col md="3" className="mb-3">
                                            <Input type="select" value={targetState} onChange={e => setTargetState(e.target.value)} className="mb-2">
                                                <option value="RESTRICTED">Escalate to Restricted</option>
                                                <option value="SUSPENDED">Escalate to Suspended</option>
                                            </Input>
                                            <Input
                                                value={confirmName}
                                                onChange={e => setConfirmName(e.target.value)}
                                                placeholder="Type account name to confirm escalation"
                                            />
                                        </Col>
                                        <Col md="4" className="mb-3">
                                            <Button
                                                color="success"
                                                outline
                                                disabled={!restoreAvailable || !reason.trim() || busy}
                                                onClick={restore}
                                                className="mr-2 mb-2"
                                            >
                                                Restore account
                                            </Button>
                                            <Button
                                                color="danger"
                                                outline
                                                disabled={!escalationAvailable || confirmName !== detail.name || !reason.trim() || busy}
                                                onClick={escalate}
                                                className="mb-2"
                                            >
                                                Escalate lifecycle
                                            </Button>
                                            <p className="text-muted small mb-0">
                                                Restore sends the restoration email. Escalation sends the matching lifecycle email and requires the account name to be typed above.
                                            </p>
                                        </Col>
                                    </Row>
                                </Panel>

                                {/* Members */}
                                <Panel title="Members">
                                    <div className="table-responsive">
                                        <Table size="sm">
                                            <thead className="thead-light">
                                            <tr>
                                                <th>Name</th>
                                                <th>Email</th>
                                                <th>Role</th>
                                                <th>Joined</th>
                                                <th>Seat activated</th>
                                                <th>Seat deactivated</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {(detail.members || []).map(member => (
                                                <tr key={member.userId}>
                                                    <td>{`${member.firstName || ""} ${member.lastName || ""}`.trim() || "—"}</td>
                                                    <td>{member.email || "—"}</td>
                                                    <td>{member.role}</td>
                                                    <td>{formatDate(member.joinedAt)}</td>
                                                    <td>{formatDate(member.seatActivatedAt)}</td>
                                                    <td>{formatDate(member.seatDeactivatedAt)}</td>
                                                </tr>
                                            ))}
                                            {(!detail.members || detail.members.length === 0) && <EmptyRow colSpan={6} />}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Panel>

                                {/* Open invitations */}
                                <Panel title="Open Invitations">
                                    <div className="table-responsive">
                                        <Table size="sm">
                                            <thead className="thead-light">
                                            <tr>
                                                <th>Email</th>
                                                <th>Role</th>
                                                <th>Expires</th>
                                                <th>Invited by</th>
                                                <th></th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {(detail.openInvitations || []).map(invitation => (
                                                <tr key={invitation.id}>
                                                    <td>{invitation.email}</td>
                                                    <td>{invitation.role}</td>
                                                    <td>{formatDate(invitation.expiresAt)}</td>
                                                    <td>{invitation.invitedByName || invitation.invitedByUserId}</td>
                                                    <td>
                                                        <Button size="sm" color="danger" outline disabled={busy} onClick={() => revokeInvitation(invitation.id)}>
                                                            Revoke
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                            {(!detail.openInvitations || detail.openInvitations.length === 0) && <EmptyRow colSpan={5} />}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Panel>

                                {/* Billing periods */}
                                <Panel title="Billing Periods">
                                    <div className="table-responsive">
                                        <Table size="sm">
                                            <thead className="thead-light">
                                            <tr>
                                                <th>Period</th>
                                                <th>Status</th>
                                                <th>Peak seats</th>
                                                <th>Stripe invoice</th>
                                                <th></th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {(detail.billingPeriods || []).map(period => (
                                                <tr key={period.id}>
                                                    <td>{period.periodStart} to {period.periodEnd}</td>
                                                    <td><Badge color={statusColor(period.status)}>{period.status}</Badge></td>
                                                    <td>{period.peakSeats ?? "—"}</td>
                                                    <td><code className="small">{short(period.stripeInvoiceId)}</code></td>
                                                    <td>
                                                        {period.overdue && (
                                                            <Button
                                                                size="sm"
                                                                color="warning"
                                                                outline
                                                                disabled={!reason.trim() || busy}
                                                                onClick={() => closePeriod(period.id)}
                                                            >
                                                                Close and invoice
                                                            </Button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                            {(!detail.billingPeriods || detail.billingPeriods.length === 0) && <EmptyRow colSpan={5} />}
                                            </tbody>
                                        </Table>
                                    </div>
                                    {detail.billingPeriods?.some(p => p.overdue) && (
                                        <p className="text-muted small mt-2 mb-0">
                                            A reason note is required before closing a billing period.
                                        </p>
                                    )}
                                </Panel>

                                {/* Enterprise invoices */}
                                <Panel title="Organisation Invoices">
                                    <div className="table-responsive">
                                        <Table size="sm">
                                            <thead className="thead-light">
                                            <tr>
                                                <th>Stripe invoice</th>
                                                <th>Period</th>
                                                <th>Status</th>
                                                <th>Peak seats</th>
                                                <th>Fee</th>
                                                <th>Created</th>
                                                <th>Paid</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {(detail.invoices || []).map(invoice => (
                                                <tr key={invoice.id}>
                                                    <td><code className="small">{short(invoice.stripeInvoiceId)}</code></td>
                                                    <td>{invoice.billingPeriodId}</td>
                                                    <td><Badge color={statusColor(invoice.status)}>{invoice.status}</Badge></td>
                                                    <td>{invoice.peakSeats}</td>
                                                    <td>{invoice.seatFeePence}p</td>
                                                    <td>{formatDate(invoice.createdAt)}</td>
                                                    <td>{formatDate(invoice.paidAt)}</td>
                                                </tr>
                                            ))}
                                            {(!detail.invoices || detail.invoices.length === 0) && <EmptyRow colSpan={7} />}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Panel>

                                {/* Seat snapshots */}
                                <Panel title="Seat Snapshots">
                                    <div className="table-responsive">
                                        <Table size="sm">
                                            <thead className="thead-light">
                                            <tr>
                                                <th>Seats</th>
                                                <th>Snapshot at</th>
                                                <th>Trigger</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {(detail.seatSnapshots || []).map(snapshot => (
                                                <tr key={snapshot.id}>
                                                    <td>{snapshot.seatCount}</td>
                                                    <td>{formatDate(snapshot.snapshotAt)}</td>
                                                    <td>{snapshot.trigger}</td>
                                                </tr>
                                            ))}
                                            {(!detail.seatSnapshots || detail.seatSnapshots.length === 0) && <EmptyRow colSpan={3} />}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Panel>

                                {/* Lifecycle event history */}
                                <Panel title="Lifecycle Event History">
                                    <div className="table-responsive">
                                        <Table size="sm">
                                            <thead className="thead-light">
                                            <tr>
                                                <th>From</th>
                                                <th>To</th>
                                                <th>Trigger</th>
                                                <th>Stripe event</th>
                                                <th>At</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {(detail.lifecycleEvents || []).map(event => (
                                                <tr key={event.id}>
                                                    <td>{event.fromState || "—"}</td>
                                                    <td>{event.toState}</td>
                                                    <td>{event.trigger}</td>
                                                    <td><code className="small">{short(event.stripeEventId)}</code></td>
                                                    <td>{formatDate(event.eventAt)}</td>
                                                </tr>
                                            ))}
                                            {(!detail.lifecycleEvents || detail.lifecycleEvents.length === 0) && <EmptyRow colSpan={5} />}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Panel>

                                {/* Resend lifecycle email */}
                                <Panel title="Resend Lifecycle Email">
                                    <Row className="align-items-center">
                                        <Col md="4" className="mb-2">
                                            <Input type="select" value={emailType} onChange={e => setEmailType(e.target.value)}>
                                                <option value="PAYMENT_FAILED">Payment failed</option>
                                                <option value="RESTRICTION_WARNING">Restriction warning</option>
                                                <option value="SUSPENSION">Suspension</option>
                                                <option value="RESTORATION">Restoration</option>
                                            </Input>
                                        </Col>
                                        <Col md="4" className="mb-2">
                                            <Button color="secondary" outline disabled={busy} onClick={resendEmail}>
                                                Send to owners
                                            </Button>
                                        </Col>
                                    </Row>
                                </Panel>

                                {/* Admin audit log */}
                                <Panel title="Admin Audit Log">
                                    <div className="table-responsive">
                                        <Table size="sm">
                                            <thead className="thead-light">
                                            <tr>
                                                <th>Action</th>
                                                <th>Admin</th>
                                                <th>Reason</th>
                                                <th>Old value</th>
                                                <th>New value</th>
                                                <th>At</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {(detail.auditLog || []).map(entry => (
                                                <tr key={entry.id}>
                                                    <td>{entry.action}</td>
                                                    <td><code className="small">{short(entry.adminUserId)}</code></td>
                                                    <td>{entry.reason || "—"}</td>
                                                    <td><code className="small">{entry.oldValue || "—"}</code></td>
                                                    <td><code className="small">{entry.newValue || "—"}</code></td>
                                                    <td>{formatDate(entry.performedAt)}</td>
                                                </tr>
                                            ))}
                                            {(!detail.auditLog || detail.auditLog.length === 0) && <EmptyRow colSpan={6} />}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Panel>
                            </>
                        )}

                                            </CardBody>
                                        </Card>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </Container>
                </section>
            </div>
        </>
    );
}
